<template>
  <v-dialog
    v-model="localDialog"
    :max-width="modalLayout.SM"
    v-bind="modalPersistentOnlyViewport"
    @click:outside="handleCancel"
  >
    <v-card>
      <CrudFormDialogHeader
        :kicker="$t('common.reports')"
        :title="$t('reportSelection.title')"
        show-close
        @close="handleCancel"
      >
        <template #append>
          <span class="solvit-modal-header-context text-right mr-2 mr-sm-4">
            <span class="text--secondary font-weight-regular">{{ $t('reportSelection.examineDateLabel') }}</span>
            <span class="solvit-modal-header-context__primary ml-1">{{ getCommonExamineDate() }}</span>
          </span>
        </template>
      </CrudFormDialogHeader>

      <v-card-subtitle class="px-4 pt-2">
        {{ $t('reportSelection.subtitle') }}
      </v-card-subtitle>

      <v-card-text class="pa-0">
        <v-list>
          <v-list-item-group
            v-model="selectedReports"
            multiple
            mandatory
          >
            <v-list-item
              v-for="(report, index) in reports"
              :key="report.id || index"
              :value="report.id"
              :disabled="loading"
            >
              <template slot="default" slot-scope="{ active }">
                <v-list-item-action>
                  <v-checkbox
                    :input-value="active"
                    color="primary"
                    :disabled="loading"
                  />
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>
                    {{
                      report.reportName ||
                        report.name ||
                        $t('reportSelection.reportFallback', { id: report.id })
                    }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="report.reportDesc || report.description">
                    {{ report.reportDesc || report.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action v-if="report.id === currentReportId">
                  <v-chip
                    small
                    color="primary"
                    text-color="text"
                  >
                    {{ $t('reportSelection.current') }}
                  </v-chip>
                </v-list-item-action>
              </template>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          outlined
          class="solvit-crud-outline-btn"
          :disabled="loading"
          @click="handleCancel"
        >
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          outlined
          class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
          :disabled="loading || selectedReports.length === 0"
          :loading="loading"
          @click="handleConfirm"
        >
          {{
            $t('reportSelection.confirmWithCount', {
              count: selectedReports.length
            })
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { MODAL_MAX_WIDTH, MODAL_PERSISTENT_ONLY_VIEWPORT } from '@/constants/modalLayout';

export default {
  name: 'ReportSelectionDialog',
  components: {
    CrudFormDialogHeader
  },

  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    reports: {
      type: Array,
      default: () => []
    },
    currentReportId: {
      type: [String, Number],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      modalLayout: MODAL_MAX_WIDTH,
      modalPersistentOnlyViewport: MODAL_PERSISTENT_ONLY_VIEWPORT,
      selectedReports: []
    };
  },

  computed: {
    localDialog: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit('update:dialog', value);
      }
    }
  },

  watch: {
    dialog(newVal) {
      if (newVal) {
        this.initializeSelection();
      }
    },
    
    reports: {
      handler(newReports) {
        if (newReports && newReports.length > 0) {
          this.initializeSelection();
        }
      },
      immediate: true
    }
  },

  methods: {
    initializeSelection() {
      // 기본적으로 현재 보고서는 선택된 상태로 시작
      if (this.currentReportId && this.reports.length > 0) {
        const currentReport = this.reports.find(r => r.id === this.currentReportId);
        if (currentReport) {
          this.selectedReports = [this.currentReportId];
        } else if (this.reports.length > 0) {
          // 현재 보고서를 찾을 수 없으면 첫 번째 보고서 선택
          this.selectedReports = [this.reports[0].id];
        }
      }
    },

    getCommonExamineDate() {
      if (this.reports.length > 0 && this.reports[0].examineDate) {
        return this.formatDate(this.reports[0].examineDate);
      }
      return this.$t('reportSelection.noExamineDate');
    },

    formatDate(dateString) {
      if (!dateString) return '';
      
      try {
        const date = new Date(dateString);
        const locale = this.$i18n.locale === 'en' ? 'en-US' : 'ko-KR';
        return date.toLocaleDateString(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      } catch (error) {
        console.error('날짜 포맷 에러:', error);
        return dateString;
      }
    },

    handleConfirm() {
      if (this.selectedReports.length === 0) {
        alert(this.$t('reportSelection.selectAtLeastOne'));
        return;
      }

      this.$emit('confirm', this.selectedReports);
    },

    handleCancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
.v-list-item--active {
  background-color: color-mix(in srgb, var(--v-primary-base) 12%, var(--v-surface-base));
}

.v-card-subtitle {
  color: var(--v-text-base);
  opacity: 0.7;
  padding-bottom: 16px;
}
</style>
